import {differenceInMilliseconds, max, min} from 'date-fns';

import type {RentalCalendarScale} from '../types/RentalCalendarScale';

interface GetEventBarLayoutParams {
  start: Date;
  end: Date;
  scale: RentalCalendarScale;
  /** Total pixel width of the timeline track (columns.length * COLUMN_WIDTH) */
  trackWidth: number;
}

export interface EventBarLayout {
  left: number;
  width: number;
  /** Event starts before the visible range (bar is clamped at the left edge) */
  isClampedStart: boolean;
  /** Event ends after the visible range (bar is clamped at the right edge) */
  isClampedEnd: boolean;
}

/**
 * Positions an event bar on the timeline by linear interpolation of its (range-clamped)
 * start/end within the visible range. Works for any scale — a month of days or a day of hours.
 */
export function getEventBarLayout(params: GetEventBarLayoutParams): EventBarLayout {
  const rangeDuration = differenceInMilliseconds(params.scale.rangeEnd, params.scale.rangeStart);

  const clampedStart = max([params.start, params.scale.rangeStart]);
  const clampedEnd = min([params.end, params.scale.rangeEnd]);

  const startFraction =
    differenceInMilliseconds(clampedStart, params.scale.rangeStart) / rangeDuration;
  const endFraction = differenceInMilliseconds(clampedEnd, params.scale.rangeStart) / rangeDuration;

  const left = Math.round(startFraction * params.trackWidth);
  const right = Math.round(endFraction * params.trackWidth);

  return {
    left,
    width: Math.max(right - left, MIN_BAR_WIDTH),
    isClampedStart: params.start < params.scale.rangeStart,
    isClampedEnd: params.end > params.scale.rangeEnd,
  };
}

/**
 * A one-hour rental is 3px wide in month view. Crowded days collapse into a chip, but a lone short
 * rental stays a bar, so the floor has to be wide enough to see and to hover.
 */
export const MIN_BAR_WIDTH = 20;
